// src/components/Dashboard.js
import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import API_URL from "../../src/Config";
import { Doughnut } from "react-chartjs-2";
import Select from "react-select";
import "./Dashboard.css"; // Import the CSS file
import "../Responsive/DashboardResponsive.css";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { ThemeContext } from "../../src/ThemeContext";
import DatePicker from "react-datepicker";
import { TextField } from "@mui/material";
import { LuFileSpreadsheet } from "react-icons/lu";
import { MdOutlineWatchLater } from "react-icons/md";
import { RxCube } from "react-icons/rx";
import { LuNotebookText } from "react-icons/lu";
import { TbMan } from "react-icons/tb";
import { LiaUserFriendsSolid } from "react-icons/lia";
import { MdKeyboardArrowDown } from "react-icons/md";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import TooltipLogic from "./TooltipLogic";
ChartJS.register(ArcElement, Tooltip, Legend);

const Dashboard = ({ isSidebarExpanded }) => {
  const [chartData, setChartData] = useState({});
  const [tableData, setTableData] = useState([]);
  const [error, setError] = useState(null);
  const { theme } = useContext(ThemeContext);
  const isDarkTheme = theme === "dark";
  const [loading, setLoading] = useState(true);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [isNoDataAvailable, setIsNoDataAvailable] = useState(false);
  const [workPermitData, setWorkPermitData] = useState({
    pending: 0,
    issued: 0,
    closed: 0,
    dateRange: {},
  });

  const [PRData, setPRData] = useState({
    totalPRs: 0,
    approvedPRs: 0,
    approvedRate: 0,
    pending: 0,
    rejected: 0,
    returned: 0,
    avgApprovalTime: 0,
    prToPOConversionRate: 0,
    dateRange: {},
  });

  const [POData, setPOData] = useState({
    total_pos: 0,
    avg_po_processing_days: 0,
    inwarded_vs_ordered_rate: 0,
    vendor_statistics: [],
    date_range: {},
  });

  const [inventoryDatas, setInventoryDatas] = useState({
    inventory_turnover_ratio: 0,
    stock_availability_rate: 0,
    reorder_compliance: 0,
    material_shortage_rate: 0,
    top_consumed_items: [],
  });

  const [preventiveData, setPreventiveData] = useState({
    completed: 0,
    scheduled: 0,
  });
  const [amcData, setAMCData] = useState({
    completed: 0,
    scheduled: 0,
  });
  const [inventoryData, setInventoryData] = useState({
    inward: 0,
    outward: 0,
  });

  const [breakdownData, setBreakdownData] = useState({
    totalEntries: 0,
  });

  const [manPowerData, setManPowerData] = useState({
    TotalAvailableManpower: 0,
    TotalBalancePosition: 0,
    TotalTenderRequirement: 0,
  });

  const filterOptions = [
    { label: "All", value: "all" },
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Tomorrow", value: "tomorrow" },
    { label: "Current Week", value: "current_week" },
    { label: "Current Month", value: "current_month" },
    { label: "Next 7 Days", value: "next7days" },
    { label: "Next Month", value: "next_month" },
    { label: "Previous Month", value: "previous_month" },
    { label: "Previous Week", value: "past_week" },
    { label: "Custom Date", value: "custom_date" },
  ];

  const [selectedFilter, setSelectedFilter] = useState(
    filterOptions.find((option) => option.value === "current_month")
  );

  const handleFilterChange = (selectedOption) => {
    setSelectedFilter(selectedOption);
  };

  const formatDate = (date) => {
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    return new Date(date)
      .toLocaleDateString("en-CA", options)
      .replace(/-/g, "/");
  };

  const fetchChartData = async (
    filter_option,
    start_date = null,
    end_date = null
  ) => {
    try {
      const authKey = localStorage.getItem("authKey");

      // Build parameters for the API request
      const params = {};

      if (filter_option.value === "custom_date" && start_date && end_date) {
        // Format start_date and end_date as yyyy-MM-dd
        params.start_date = formatDate(start_date);
        params.end_date = formatDate(end_date);
      } else {
        // Use the value of filter_option for non-custom_date filters
        params.filter_option = filter_option.value;
      }

      const response = await axios.get(
        `${API_URL}/api/todays_due_preventive_schedules`,
        {
          headers: { Authorization: authKey },
          params: params,
        }
      );

      const reportData = response.data.report || {};

      // Check if all data is null or 0
      const isNoDataAvailable =
        !reportData.active &&
        !reportData.cancelled &&
        !reportData.completed &&
        !reportData.hold &&
        !reportData.in_progress &&
        !reportData.not_in_tenure &&
        !reportData.overdue &&
        reportData.total_schedules === 0;

      // Set the state based on the fetched data
      setChartData({
        active: reportData.active || 0,
        cancelled: reportData.cancelled || 0,
        completed: reportData.completed || 0,
        hold: reportData.hold || 0,
        in_progress: reportData.in_progress || 0,
        not_in_tenure: reportData.not_in_tenure || 0,
        overdue: reportData.overdue || 0,
        total_schedules: reportData.total_schedules || 0,
      });

      // Set the no data flag
      setIsNoDataAvailable(isNoDataAvailable);
    } catch (err) {
      console.error("Error fetching chart data:", err);
      setError("Failed to fetch chart data.");
    }
  };

  useEffect(() => {
    if (
      selectedFilter.value === "custom_date" &&
      selectedStartDate &&
      selectedEndDate
    ) {
      fetchChartData(selectedFilter, selectedStartDate, selectedEndDate);
    } else {
      fetchChartData(selectedFilter);
    }
  }, [selectedFilter, selectedStartDate, selectedEndDate]);

  const fetchTableData = async (
    filter_option,
    start_date = null,
    end_date = null
  ) => {
    try {
      const authKey = localStorage.getItem("authKey");

      // Build parameters for the API request
      const params = {};

      if (filter_option.value === "custom_date" && start_date && end_date) {
        // Format start_date and end_date as yyyy-MM-dd
        params.start_date = formatDate(start_date);
        params.end_date = formatDate(end_date);
      } else {
        params.filter_option = filter_option.value;
      }

      const response = await axios.get(`${API_URL}/api/schedule_report`, {
        headers: { Authorization: authKey },
        params: params, // Pass parameters in the URL query
      });

      // Ensure that tableData is an array, and if not, set it as an empty array
      setTableData(
        Array.isArray(response.data.report) ? response.data.report : []
      );
    } catch (err) {
      setError(err); // Handle errors
    }
  };

  useEffect(() => {
    if (
      selectedFilter.value === "custom_date" &&
      selectedStartDate &&
      selectedEndDate
    ) {
      fetchTableData(selectedFilter, selectedStartDate, selectedEndDate);
    } else {
      fetchTableData(selectedFilter);
    }
  }, [selectedFilter, selectedStartDate, selectedEndDate]);

  const fetchReportData = async (
    filter_option,
    start_date = null,
    end_date = null
  ) => {
    try {
      const authKey = localStorage.getItem("authKey");

      // Build parameters for the API request
      const params = {};

      if (filter_option.value === "custom_date" && start_date && end_date) {
        // Format start_date and end_date as yyyy-MM-dd
        params.start_date = formatDate(start_date);
        params.end_date = formatDate(end_date);
      } else {
        params.filter_option = filter_option.value;
      }

      const response = await axios.get(`${API_URL}/api/kpi/summary/reports`, {
        headers: { Authorization: authKey }, // Correct placement of headers
        params: params, // Pass parameters in the URL query
      });
      const reportData = response.data.report;

      // Set the extracted data to state
      setPreventiveData({
        completed: reportData.preventive_schedules?.completed_schedules || 0,
        scheduled: reportData.preventive_schedules?.scheduled || 0,
        completionRate: reportData.preventive_schedules?.completion_rate || 0,
      });
      setAMCData({
        completed: reportData.amc_schedules?.completed_schedules || 0,
        scheduled: reportData.amc_schedules?.scheduled || 0,
        completionRate: reportData.amc_schedules?.completion_rate || 0,
        missedRate: reportData.amc_schedules?.missed_rate || 0,
      });
      setInventoryData({
        inward: reportData.inventory_data?.total_inwarded_qty || 0,
        outward: reportData.inventory_data?.total_outwarded_qty || 0,
      });

      setBreakdownData({
        totalEntries: reportData.breakdown_schedules?.total_entries || 0,
      });

      setLoading(false);
    } catch (err) {
      console.error("Error fetching report data:", err);
      setError("Failed to fetch data");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      selectedFilter.value === "custom_date" &&
      selectedStartDate &&
      selectedEndDate
    ) {
      fetchReportData(selectedFilter, selectedStartDate, selectedEndDate);
    } else {
      fetchReportData(selectedFilter);
    }
  }, [selectedFilter, selectedStartDate, selectedEndDate]);

  const fetchWorkPermitStatus = async (
    filter_option,
    start_date = null,
    end_date = null,
    permit_type_id = null
  ) => {
    try {
      const authKey = localStorage.getItem("authKey");

      const params = {};

      if (filter_option.value === "custom_date" && start_date && end_date) {
        params.start_date = formatDate(start_date); // Format: yyyy-MM-dd
        params.end_date = formatDate(end_date);
      } else {
        params.filter_option = filter_option.value;
      }

      if (permit_type_id) {
        params.permit_type_id = permit_type_id;
      }

      const response = await axios.get(
        `${API_URL}/api/work_permit/status_statistics`,
        {
          headers: {
            Authorization: authKey,
          },
          params: params,
        }
      );

      const reportData = response.data.report;
      const pending = parseInt(reportData.pending) || 0;
      const issued = parseInt(reportData.issued) || 0;
      const closed = parseInt(reportData.closed) || 0;
      const rejected = parseInt(reportData.rejected) || 0;

      const total = pending + issued + closed + rejected;
      // Set state or do further processing with data
      setWorkPermitData({
        pending: reportData.pending || 0,
        issued: reportData.issued || 0,
        closed: reportData.closed || 0,
        rejected: reportData.rejected || 0,
        dateRange: reportData.date_range || {},
        total: reportData.total || 0,

        pendingPercentage: total ? (pending / total) * 100 : 0,
        issuedPercentage: total ? (issued / total) * 100 : 0,
        closedPercentage: total ? (closed / total) * 100 : 0,
        rejectedPercentage: total ? (rejected / total) * 100 : 0,
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching work permit status:", error);
      setError("Failed to fetch work permit status data");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      selectedFilter.value === "custom_date" &&
      selectedStartDate &&
      selectedEndDate
    ) {
      fetchWorkPermitStatus(selectedFilter, selectedStartDate, selectedEndDate);
    } else {
      fetchWorkPermitStatus(selectedFilter);
    }
  }, [selectedFilter, selectedStartDate, selectedEndDate]);

  const fetchPRStatusReport = async (
    filter_option,
    start_date = null,
    end_date = null
  ) => {
    try {
      const authKey = localStorage.getItem("authKey");

      const params = {};

      if (filter_option.value === "custom_date" && start_date && end_date) {
        params.start_date = formatDate(start_date); // "yyyy-MM-dd"
        params.end_date = formatDate(end_date);
      } else {
        params.filter_option = filter_option.value;
      }

      const response = await axios.get(
        `${API_URL}/api/purchase_request/status_statistics`,
        {
          headers: {
            Authorization: authKey,
          },
          params,
        }
      );
      const reportData = response.data.report;

      // Parse all numeric values from strings
      const totalPRs = parseInt(reportData.total_prs) || 0;
      const approved_prs = parseInt(reportData.approved_prs) || 0;
      const pending = parseInt(reportData.pending) || 0;
      const rejected = parseInt(reportData.rejected) || 0;
      const returned = parseInt(reportData.returned) || 0;
      const draft = parseInt(reportData.draft) || 0;
      const approved_rate = parseFloat(reportData.approved_rate) || 0;
      const avgApprovalTime =
        parseFloat(reportData.avg_approval_time_hours) || 0;
      const prToPOConversionRate =
        parseFloat(reportData.pr_to_po_conversion_rate) || 0;

      // Update state with result
      setPRData({
        totalPRs,
        approvedPRs: approved_prs,
        approvedRate: approved_rate,
        pending,
        rejected,
        returned,
        draft,
        avgApprovalTime,
        prToPOConversionRate,
        dateRange: reportData.date_range || {},
        approvedPRsPercentage: totalPRs ? (approved_prs / totalPRs) * 100 : 0,
        pendingPercentage: totalPRs ? (pending / totalPRs) * 100 : 0,
        rejectedPercentage: totalPRs ? (rejected / totalPRs) * 100 : 0,
        returnedPercentage: totalPRs ? (returned / totalPRs) * 100 : 0,
        draftedPercentage: totalPRs ? (draft / totalPRs) * 100 : 0,
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching PR report:", error);
      setError("Failed to fetch PR report data");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      selectedFilter.value === "custom_date" &&
      selectedStartDate &&
      selectedEndDate
    ) {
      fetchPRStatusReport(selectedFilter, selectedStartDate, selectedEndDate);
    } else {
      fetchPRStatusReport(selectedFilter);
    }
  }, [selectedFilter, selectedStartDate, selectedEndDate]);

  const fetchPurchaseOrderStats = async (
    filter_option,
    start_date = null,
    end_date = null
  ) => {
    try {
      const authKey = localStorage.getItem("authKey");

      // Prepare query params
      const params = {};

      if (filter_option.value === "custom_date" && start_date && end_date) {
        params.start_date = formatDate(start_date); // "yyyy-MM-dd"
        params.end_date = formatDate(end_date);
      } else {
        params.filter_option = filter_option.value;
      }

      const response = await axios.get(
        `${API_URL}/api/purchase_order/status_statistics`,
        {
          headers: {
            Authorization: authKey,
          },
          params: params,
        }
      );

      const reportData = response.data.report;

      // Set data in state
      setPOData({
        total_pos: reportData.total_pos || 0,
        avg_po_processing_days: reportData.avg_po_processing_days || 0,
        inwarded_vs_ordered_rate: reportData.inwarded_vs_ordered_rate || 0,
        vendor_statistics: reportData.vendor_statistics || [],
        date_range: reportData.date_range,
      });

      setLoading(false);
    } catch (err) {
      console.error("Error fetching PO statistics:", err);
      setError("Failed to fetch PO statistics.");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      selectedFilter.value === "custom_date" &&
      selectedStartDate &&
      selectedEndDate
    ) {
      fetchPurchaseOrderStats(
        selectedFilter,
        selectedStartDate,
        selectedEndDate
      );
    } else {
      fetchPurchaseOrderStats(selectedFilter);
    }
  }, [selectedFilter, selectedStartDate, selectedEndDate]);

  const fetchInventoryReportSummary = async () => {
    try {
      const authKey = localStorage.getItem("authKey");

      const response = await axios.get(
        `${API_URL}/api/inventory/report_summary`,
        {
          headers: {
            Authorization: authKey,
          },
        }
      );

      const report = response.data.report;

      // Update state with the fetched report
      setInventoryDatas({
        inventory_turnover_ratio: report.inventory_turnover_ratio || 0,
        stock_availability_rate: report.stock_availability_rate || 0,
        reorder_compliance: report.reorder_compliance || 0,
        material_shortage_rate: report.material_shortage_rate || 0,
        top_consumed_items: report.top_consumed_items || [],
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching inventory report summary:", error);
      setError("Failed to fetch inventory report.");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryReportSummary();
  }, []);

  function formatHoursToDays(hours) {
    const totalHours = parseFloat(hours);
    if (isNaN(totalHours)) return "N/A";

    const days = Math.floor(totalHours / 24);
    const remainingHours = Math.round(totalHours % 24);

    return `${days} days, ${remainingHours} hours`;
  }

  const doughnutChartData = {
    labels: [
      "Active",
      "Cancelled",
      "Completed",
      "Hold",
      "In Progress",
      "Not in Tenure",
      "Overdue",
    ],
    datasets: [
      {
        data: [
          chartData?.active || 0,
          chartData?.cancelled || 0,
          chartData?.completed || 0,
          chartData?.hold || 0,
          chartData?.in_progress || 0,
          chartData?.not_in_tenure || 0,
          chartData?.overdue || 0,
        ],
        backgroundColor: [
          "#2980b9", // Active
          "#f81700", // Cancelled
          "#2e8b57", // Completed
          "#d4ac0d", // Hold
          "#f4a462", // In Progress
          "#313639", // Not in Tenure
          "#c0612b", // Overdue
        ],
        borderColor: "#fff",
        borderWidth: 1,
      },
    ],
  };

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "78%",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: isDarkTheme ? "#333" : "#f5f5f5",
        bodyColor: isDarkTheme ? "#FFFFFF" : "#000",
        borderColor: "#ddd",
        borderWidth: 1,
        titleColor: isDarkTheme ? "#FFFFFF" : "#000",
        padding: 10,
        zIndex: 10000,
        callbacks: {
          label: function (tooltipItem) {
            return `${tooltipItem.label}: ${tooltipItem.raw}`;
          },
        },
      },
    },
    layout: {
      padding: {
        top: 20,
        bottom: 20,
      },
    },
    elements: {
      arc: {
        borderRadius: 1,
        spacing: 0,
      },
    },
  };

  const labels = doughnutChartData.labels;
  const colors = doughnutChartData.datasets[0].backgroundColor;

  const CircularProgress = ({ percentage }) => {
    const radius = 45;
    const stroke = 8;
    const normalizedRadius = radius - stroke / 2;
    const circumference = 2 * Math.PI * normalizedRadius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div className="progress-wrapper">
        <svg height="100" width="100">
          <circle
            stroke="#233b80"
            fill="transparent"
            strokeWidth={stroke}
            r={normalizedRadius}
            cx="50"
            cy="50"
          />
          <circle
            stroke="#26bf94"
            fill="transparent"
            strokeWidth={stroke}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            r={normalizedRadius}
            cx="50"
            cy="50"
            className="progress-ring"
          />
        </svg>
        <div className="progress-text">{percentage}%</div>
      </div>
    );
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.get(
        `${API_URL}/api/reports/manpower_totals`,
        {
          headers: {
            Authorization: authKey,
          },
        }
      );

      setManPowerData({
        TotalAvailableManpower: response.data.TotalAvailableManpower || 0,
        TotalBalancePosition: response.data.TotalBalancePosition || 0,
        TotalTenderRequirement: response.data.TotalTenderRequirement || 0,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching customers data:", error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  function formatNumber(number) {
    if (number === undefined || number === null || isNaN(number)) {
      return "0";
    }

    const parts = number.toString().split(".");
    const integerPart = parts[0];
    const decimalPart = parts.length > 1 ? "." + parts[1] : "";

    let formattedIntegerPart = "";
    let lakhFlag = false;
    let croreFlag = false;
    let croreLakhFlag = false;

    for (let i = integerPart.length - 1, count = 0; i >= 0; i--, count++) {
      if (count === 3 && !lakhFlag) {
        formattedIntegerPart = "," + formattedIntegerPart;
        count = 0;
        lakhFlag = true;
      } else if (count === 2 && lakhFlag && !croreFlag) {
        formattedIntegerPart = "," + formattedIntegerPart;
        count = 0;
        croreFlag = true;
      } else if (count === 2 && croreFlag && !croreLakhFlag) {
        formattedIntegerPart = "," + formattedIntegerPart;
        count = 0;
        croreLakhFlag = true;
      }

      formattedIntegerPart = integerPart[i] + formattedIntegerPart;
    }

    return formattedIntegerPart + decimalPart;
  }

  return (
    <div
      className={`dashboard  ${isSidebarExpanded ? "expanded" : "collapsed"}`}
    >
      <div className="data-form-content">
        <div className="data-form-header1">
          <div className="data-form-header-content">
            <div className="data-form-heading-content-left-side">Dashboard</div>
            <div className="data-form-heading-content-right-side">
              <div className="dropdown-container">
                <Select
                  value={selectedFilter}
                  onChange={handleFilterChange}
                  options={filterOptions}
                  // className="project-dropdown"
                />
              </div>
              {selectedFilter.value === "custom_date" && (
                <div className="input-group-datepickers">
                  <DatePicker
                    placeholderText="Select Date Range"
                    className="dates"
                    startDate={selectedStartDate}
                    endDate={selectedEndDate}
                    selectsRange={true}
                    dateFormat="dd/MM/yyyy"
                    isClearable
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    onChange={(dates) => {
                      const [start, end] = dates;
                      setSelectedStartDate(start);
                      setSelectedEndDate(end);
                    }}
                    renderInput={(startProps, endProps) => (
                      <>
                        <TextField {...startProps} variant="standard" />
                        <TextField {...endProps} variant="standard" />
                      </>
                    )}
                    startText="Start Date"
                    endText="End Date"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {/* <div className="header-separators"></div> */}

        <div className="dashboard-container">
          <div className="dashboard-container-content">
            <div className="dashboard-container-first-part">
              <div className="metrics-container">
                <div className="metric-card progress-card">
                  <div className="progress-content1">
                    <div className="progress-heading">Target Progress</div>
                    <p>
                      You have completed Preventive Schedules{" "}
                      <span className="highlight">
                        {preventiveData.completionRate}%
                      </span>{" "}
                      of the given target
                    </p>
                    {/* <a href="#" className="view-details">View details</a> */}
                    <div className="view-details-flex">
                      {/* <div className="view-details">View details</div>  */}
                      {/* <div className="progress-circle">
              <span className="progress-percentage">{preventiveData.completionRate}%</span>
            </div> */}
                    </div>
                  </div>
                  <div className="progress-content2">
                    <div className="view-details-flex">
                      {/* <div className="progress-circle">
                        <span className="progress-percentage">
                          {preventiveData.completionRate}%
                        </span>
                       

                      </div> */}
                      <CircularProgress
                        percentage={preventiveData.completionRate}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="metrics-container1">
                <div className="metrics-container-part1">
                  <div className="metric-card">
                    <div className="" style={{ float: "right" }}>
                      <TooltipLogic
                        largIcons
                        tooltiplogic="The total number of purchase requests created within the selected date range."
                      />
                    </div>
                    <div className="card-header">
                      <div className="icon-circle blue-bg">
                        <span className="">
                          <LuFileSpreadsheet className="document-icon" />
                        </span>
                      </div>
                      {/* <TooltipLogic tooltiplogic="The total number of purchase requests created within the selected date range." /> */}
                      <div>
                        <div className="metric-title">Purchase Requests</div>
                        <div className="metric-value">{PRData.totalPRs}</div>
                      </div>
                    </div>
                    <div className="chart-div">
                      {/* <div className="chart blue-chart"></div> */}
                      <div className="metric-stats">
                        <span className="approval-rate">
                          {PRData.approvedRate}% approval rate{" "}
                          <TooltipLogic
                            smallIcons
                            blue
                            //                             tooltiplogic="Percentage of approved PRs out of the total PRs.
                            //  Formula: (Approved PRs / Total PRs) × 100"
                            tooltiplogic={
                              <>
                                Percentage of approved PRs out of the total PRs.
                                <br />
                                <strong>Formula:</strong>
                                <br />
                                <code
                                  style={{
                                    background: "#f5f5f5",
                                    padding: "2px 4px",
                                    borderRadius: "4px",
                                    display: "inline-block",
                                  }}
                                >
                                  (Approved PRs / Total PRs) × 100
                                </code>
                              </>
                            }
                          />
                        </span>
                        {/* <span className="positive-change">+25% this month</span> */}
                      </div>
                    </div>
                  </div>

                  <div className="metric-card">
                    <div className="" style={{ float: "right" }}>
                      <TooltipLogic
                        largIcons
                        // tooltiplogic="Completion Rate = (Completed AMC ÷ Total AMC Scheduled) × 100 Indicates how many AMC tasks were completed out of all scheduled ones."
                        tooltiplogic={
                          <>
                            Indicates how many AMC tasks were completed out of
                            all scheduled ones.
                            <br />
                            <strong>Formula:</strong>
                            <br />
                            <code
                              style={{
                                background: "#f5f5f5",
                                padding: "2px 4px",
                                borderRadius: "4px",
                                display: "inline-block",
                              }}
                            >
                              Completion Rate = (Completed AMC ÷ Total AMC
                              Scheduled) × 100
                            </code>
                          </>
                        }
                      />
                    </div>
                    <div className="card-header">
                      <div className="icon-circle yellow-bg">
                        <span className="">
                          <MdOutlineWatchLater className="clock-icon" />
                        </span>
                      </div>
                      <div>
                        <div className="metric-title">AMC Completion Rate</div>
                        <div className="metric-value">
                          {amcData.completionRate}%
                        </div>
                      </div>
                    </div>
                    <div className="chart-div"></div>
                    {/* <div className="chart yellow-chart"></div> */}
                    <div className="metric-stats">
                      <span className="missed-rate">
                        {amcData.missedRate}% missed{" "}
                        <TooltipLogic
                          smallIcons
                          yellow
                          //                           tooltiplogic="Missed Rate = (Missed AMC ÷ Total AMC Scheduled) × 100
                          //  Shows how many AMC tasks were not completed out of all scheduled ones."
                          tooltiplogic={
                            <>
                              Shows how many AMC tasks were not completed out of
                              all scheduled ones.
                              <br />
                              <strong>Formula:</strong>
                              <br />
                              <code
                                style={{
                                  background: "#f5f5f5",
                                  padding: "2px 4px",
                                  borderRadius: "4px",
                                  display: "inline-block",
                                }}
                              >
                                Missed Rate = (Missed AMC ÷ Total AMC Scheduled)
                                × 100
                              </code>
                            </>
                          }
                        />
                      </span>
                      {/* <span className="positive-change">+3.2% this month</span> */}
                    </div>
                  </div>

                  <div className="metric-card">
                    <div className="" style={{ float: "right" }}>
                      <TooltipLogic
                        largIcons
                        // tooltiplogic="Indicates the percentage of inventory that is currently available.Formula: (Available Quantity ÷ Total Quantity) × 100 Measures the readiness of stock to fulfill demand"
                        tooltiplogic={
                          <>
                            Indicates the percentage of inventory that is
                            currently available. Measures the readiness of stock
                            to fulfill demand.
                            <br />
                            <strong>Formula:</strong>
                            <br />
                            <code
                              style={{
                                background: "#f5f5f5",
                                padding: "2px 4px",
                                borderRadius: "4px",
                                display: "inline-block",
                              }}
                            >
                              (Available Quantity ÷ Total Quantity) × 100
                            </code>
                          </>
                        }
                      />
                    </div>
                    <div className="card-header">
                      <div className="icon-circle green-bg">
                        <span className="">
                          <RxCube className="box-icon" />
                        </span>
                      </div>
                      <div>
                        <div className="metric-title">
                          Stock Availability Rate
                        </div>
                        <div className="metric-value">
                          {inventoryDatas.stock_availability_rate}%
                        </div>
                      </div>
                    </div>
                    <div className="chart-div">
                      {/* <div className="chart green-chart"></div> */}
                      <div className="metric-stats">
                        <span className="shortage-rate">
                          {inventoryDatas.material_shortage_rate}% shortage{" "}
                          {/* <TooltipLogic
                            smallIcons
                            green
                            tooltiplogic="Shows the percentage of materials where available quantity is less than the quantity needed (pending).Formula:(Number of Shortage Items ÷ Total Items) × 100"
                          /> */}
                          <TooltipLogic
                            smallIcons
                            green
                            tooltiplogic={
                              <>
                                Shows the percentage of materials where
                                available quantity is less than the quantity
                                needed (pending).
                                <br />
                                <strong>Formula:</strong>
                                <br />
                                <code
                                  style={{
                                    background: "#f5f5f5",
                                    padding: "2px 4px",
                                    borderRadius: "4px",
                                    display: "inline-block",
                                  }}
                                >
                                  (Number of Shortage Items ÷ Total Items) × 100
                                </code>
                              </>
                            }
                          />
                        </span>
                        {/* <span className="negative-change">
                          -2.1% this month
                        </span> */}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="metrics-container-part2">
                  <div className="metric-card">
                    <div className="" style={{ float: "right" }}>
                      <TooltipLogic
                        largIcons
                        //                         tooltiplogic="Total Tender Requirement = Sum of all manpower positions required as per tender
                        //  Represents the total number of positions planned across all or selected projects."
                        tooltiplogic={
                          <>
                            Represents the total number of positions planned
                            across all or selected projects.
                            <br />
                            <strong>Formula:</strong>
                            <br />
                            <code
                              style={{
                                background: "#f5f5f5",
                                padding: "2px 4px",
                                borderRadius: "4px",
                                display: "inline-block",
                              }}
                            >
                              Total Tender Requirement = Sum of all manpower
                              positions required as per tender
                            </code>
                          </>
                        }
                      />
                    </div>
                    <div className="card-header">
                      <div className="icon-circle purple-bg">
                        <span className="">
                          <LuNotebookText className="document-icon" />
                        </span>
                      </div>
                      <div>
                        <div className="metric-title">Total Requirements</div>
                        <div className="metric-value">
                          {formatNumber(manPowerData.TotalTenderRequirement)}
                        </div>
                      </div>
                    </div>
                    <div className="chart-div">
                      {/* <div className="chart blue-chart"></div> */}
                      {/* <div className="metric-stats">
                        <span className="approval-rate">
                          {PRData.approvedRate}% approval rate
                        </span>
                        <span className="positive-change">+25% this month</span>
                      </div> */}
                    </div>
                  </div>

                  <div className="metric-card">
                    <div className="" style={{ float: "right" }}>
                      <TooltipLogic
                        largIcons
                        //                         tooltiplogic="Balance = Total Tender Requirement − Total Available Manpower
                        //  Shows the gap between required positions and currently available manpower (either on-site or appointed)."
                        tooltiplogic={
                          <>
                            Shows the gap between required positions and
                            currently available manpower (either on-site or
                            appointed).
                            <br />
                            <strong>Formula:</strong>
                            <br />
                            <code
                              style={{
                                background: "#f5f5f5",
                                padding: "2px 4px",
                                borderRadius: "4px",
                                display: "inline-block",
                              }}
                            >
                              Balance = Total Tender Requirement − Total
                              Available Manpower
                            </code>
                          </>
                        }
                      />
                    </div>
                    <div className="card-header">
                      <div className="icon-circle red-bg">
                        <span className="">
                          <TbMan className="clock-icon" />
                        </span>
                      </div>
                      <div>
                        <div className="metric-title">Balance Position</div>
                        <div className="metric-value">
                          {formatNumber(manPowerData.TotalBalancePosition)}
                        </div>
                      </div>
                    </div>
                    <div className="chart-div"></div>
                    {/* <div className="chart yellow-chart"></div> */}
                    {/* <div className="metric-stats">
                      <span className="missed-rate">
                        {amcData.missedRate}% missed
                      </span>
                      <span className="positive-change">+3.2% this month</span>
                    </div> */}
                  </div>

                  <div className="metric-card">
                    <div className="" style={{ float: "right" }}>
                      <TooltipLogic
                        largIcons
                        //                         tooltiplogic="Total Available = Present at Site + Appointment Issued (Yet to Join)
                        //  Reflects manpower either currently on-site or officially appointed but not yet joined."
                        tooltiplogic={
                          <>
                            Reflects manpower either currently on-site or
                            officially appointed but not yet joined.
                            <br />
                            <strong>Formula:</strong>
                            <br />
                            <code
                              style={{
                                background: "#f5f5f5",
                                padding: "2px 4px",
                                borderRadius: "4px",
                                display: "inline-block",
                              }}
                            >
                              Total Available = Present at Site + Appointment
                              Issued (Yet to Join)
                            </code>
                          </>
                        }
                      />
                    </div>
                    <div className="card-header">
                      <div className="icon-circle rama-bg">
                        <span className="">
                          <LiaUserFriendsSolid className="box-icon" />
                        </span>
                      </div>
                      <div>
                        <div className="metric-title">Available ManPower</div>
                        <div className="metric-value">
                          {formatNumber(manPowerData.TotalAvailableManpower)}
                        </div>
                      </div>
                    </div>
                    <div className="chart-div">
                      {/* <div className="chart green-chart"></div> */}
                      {/* <div className="metric-stats">
                        <span className="shortage-rate">
                          {inventoryDatas.material_shortage_rate}% shortage
                        </span>
                        <span className="negative-change">
                          -2.1% this month
                        </span>
                      </div> */}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard-container-second-parts">
              <div className="dashboard-panel box-1">
                <div className="panel-header">
                  <div className="header-left">
                    <div
                      className="p-indicator"
                      // style={{ backgroundColor: "#FF9800" }}
                    ></div>
                    <div className="maintenace-header-text">
                      Maintenance Metrics
                    </div>
                  </div>
                  <div className="view-details">
                    <span>View All</span>
                    <span className="arrow">
                      <MdKeyboardArrowDown className="arrow-icon" />
                    </span>
                  </div>
                </div>
                <div className="header-separators"></div>

                {/* Progress Bars */}
                <div className="maintenace-metrics-container">
                  <div className="maintenace-metric-item">
                    <div className="maintenace-metric-label">
                      Preventive Schedule Completion
                    </div>
                    <div className="maintenace-metric-value">
                      {preventiveData.completionRate}%
                    </div>
                  </div>
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar"
                      style={{
                        width: `${preventiveData.completionRate}%`,
                        backgroundColor: "#FF9800",
                      }}
                    ></div>
                  </div>

                  <div className="maintenace-metric-item">
                    <div className="maintenace-metric-label">
                      AMC Schedule Completion
                    </div>
                    <div className="maintenace-metric-value">
                      {amcData.completionRate}%
                    </div>
                  </div>
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar"
                      style={{
                        width: `${amcData.completionRate}%`,
                        backgroundColor: "#FF9800",
                      }}
                    ></div>
                  </div>

                  <div className="maintenace-metric-item">
                    <div className="maintenace-metric-label">
                      Missed AMC Schedule Rate
                    </div>
                    <div className="maintenace-metric-value red">
                      {amcData.missedRate}%
                    </div>
                  </div>
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar"
                      style={{
                        width: `${amcData.missedRate}%`,
                        backgroundColor: "#f44336",
                      }}
                    ></div>
                  </div>

                  {/* Stats Cards */}
                  <div className="stats-container">
                    <div
                      className="stat-card yellow-bg-m"
                      style={{ position: "relative" }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                        }}
                      >
                        {/* <TooltipLogic /> */}
                      </div>
                      <div className="stat-title">
                        {/* <span>
                          <TooltipLogic />
                        </span> */}
                        AMC Schedules
                      </div>
                      <div className="stat-value">
                        {formatNumber(amcData.scheduled)}
                      </div>
                      {/* <div className="stat-change">+24 from last month</div> */}
                    </div>

                    <div className="stat-card red-bg-m">
                      <div className="stat-title">Missed Schedules</div>
                      <div className="stat-value red-text">
                        {formatNumber(amcData.missedRate)}
                      </div>
                      {/* <div className="stat-change">-3 from last month</div> */}
                    </div>
                  </div>
                </div>
              </div>
              <div className="dashboard-panel box-2">
                <div className="panel-header">
                  <div className="header-left">
                    <div
                      className="p-indicator"
                      // style={{ backgroundColor: "#9C27B0" }}
                    ></div>
                    <div className="maintenace-header-text">
                      Work Permit Status
                    </div>
                    <TooltipLogic
                      largIcons
                      tooltipList={[
                        {
                          key: "Total",
                          value:
                            "Total = Pending + Issued + Closed + Rejected Represents the total number of permits created in the selected date range.",
                        },
                        {
                          key: "Approved",
                          value:
                            "Permits that have been officially approved and issued for execution.",
                        },
                        {
                          key: "Pending",
                          value:
                            "Includes statuses: Submitted, In Review, Pending Approval, Safety Approved These permits are still under process and not yet issued or closed.",
                        },
                        {
                          key: "Rejected",
                          value:
                            "Permits that were reviewed and denied approval.",
                        },
                        {
                          key: "Closed",
                          value:
                            "Work permits that have been completed and formally closed.",
                        },
                      ]}
                    />
                  </div>
                  <div className="view-details">
                    <span>View All</span>
                    <span className="arrow">
                      <MdKeyboardArrowDown className="arrow-icon" />
                    </span>
                  </div>
                </div>
                <div className="header-separators"></div>
                <div className="permit-body">
                  <div className="permit-header">
                    <div className="permit-count">{workPermitData.total}</div>
                    <div className="permit-change">
                      {/*<span className="green">+1.02%</span>*/}
                      {/* <span className="gray">compared to last week</span> */}
                    </div>
                  </div>
                  <div className="status-bar-container">
                    <div
                      className="status-bar-segment"
                      style={{
                        backgroundColor: "#4CAF50",
                        width: `${workPermitData.issuedPercentage}%`,
                      }}
                    ></div>
                    <div
                      className="status-bar-segment"
                      style={{
                        backgroundColor: "#2196F3",
                        width: `${workPermitData.pendingPercentage}%`,
                      }}
                    ></div>
                    <div
                      className="status-bar-segment"
                      style={{
                        backgroundColor: "#FF9800",
                        width: `${workPermitData.closedPercentage}%`,
                      }}
                    ></div>
                    <div
                      className="status-bar-segment"
                      style={{
                        backgroundColor: "#f44336",
                        width: `${workPermitData.rejectedPercentage}%`,
                      }}
                    ></div>
                  </div>

                  {/* Status Items */}
                  <div className="status-container">
                    <div className="status-item">
                      <div
                        className="status-indicator"
                        style={{ backgroundColor: "#4CAF50" }}
                      ></div>
                      <div className="status-label">Approved</div>
                      <div className="status-count">
                        {workPermitData.issued} permits
                      </div>
                      {/* <div className="status-percentage green">(23%)</div> */}
                    </div>

                    <div className="status-item">
                      <div
                        className="status-indicator"
                        style={{ backgroundColor: "#2196F3" }}
                      ></div>
                      <div className="status-label">Pending</div>
                      <div className="status-count">
                        {workPermitData.pending} permits
                      </div>
                      {/* <div className="status-percentage blue">(25%)</div> */}
                    </div>

                    <div className="status-item">
                      <div
                        className="status-indicator"
                        style={{ backgroundColor: "#FF9800" }}
                      ></div>
                      <div className="status-label">Closed</div>
                      <div className="status-count">
                        {workPermitData.closed} permits
                      </div>
                      {/* <div className="status-percentage orange">(36%)</div> */}
                    </div>

                    <div className="status-item">
                      <div
                        className="status-indicator"
                        style={{ backgroundColor: "#f44336" }}
                      ></div>
                      <div className="status-label">Rejected</div>
                      <div className="status-count">
                        {workPermitData.rejected} permits
                      </div>
                      {/* <div className="status-percentage red">(16%)</div> */}
                    </div>
                  </div>

                  {/* Status Bar */}
                </div>
              </div>
              <div className="dashboard-panel box-3">
                <div className="panel-header">
                  <div className="header-left">
                    <div
                      className="p-indicator"
                      // style={{ backgroundColor: "#9C27B0" }}
                    ></div>
                    <div className="maintenace-header-text">PR Status</div>
                    <TooltipLogic
                      largIcons
                      tooltipList={[
                        {
                          key: "Total Purchase Requests",
                          value:
                            "The total number of purchase requests created within the selected date range.",
                        },
                        {
                          key: "Approved",
                          value:
                            "PRs that have successfully passed the approval workflow.",
                        },
                        {
                          key: "Pending",
                          value:
                            "PRs that are submitted but awaiting review or approval.",
                        },
                        {
                          key: "Rejected",
                          value: "PRs that were reviewed and denied approval.",
                        },
                        {
                          key: "Returned",
                          value:
                            "PRs that were sent back to the requester for modifications or clarifications.",
                        },
                        {
                          key: "Deft",
                          value:
                            "PRs that have been created but not yet submitted for approval.",
                        },
                      ]}
                    />
                  </div>
                  <div className="view-details">
                    <span>View All</span>
                    <span className="arrow">
                      <MdKeyboardArrowDown className="arrow-icon" />
                    </span>
                  </div>
                </div>
                <div className="header-separators"></div>
                <div className="permit-body">
                  <div className="permit-header">
                    <div className="permit-count">
                      {formatNumber(PRData.totalPRs)}
                    </div>
                    <div className="permit-change">
                      {/* <span className="green">+1.02%</span> */}
                      {/* <span className="gray">compared to last week</span> */}
                    </div>
                  </div>

                  <div className="status-bar-container">
                    {/* <div
                      className="status-bar-segment"
                      style={{
                        backgroundColor: "#4CAF50",
                        width: `${PRData.draft}`,
                      }}
                    ></div> */}
                    <div
                      className="status-bar-segment"
                      style={{
                        backgroundColor: "#4CAF50",
                        width: `${PRData.approvedPRsPercentage}%`,
                      }}
                    ></div>
                    <div
                      className="status-bar-segment"
                      style={{
                        backgroundColor: "#2196F3",
                        width: `${PRData.pendingPercentage}%`,
                      }}
                    ></div>
                    <div
                      className="status-bar-segment"
                      style={{
                        backgroundColor: "#a72d24",
                        width: `${PRData.returnedPercentage}%`,
                      }}
                    ></div>
                    {/* <div
                      className="status-bar-segment"
                      style={{
                        backgroundColor: "#f443385",
                        width: `${PRData.rejectedPercentage}%`,
                      }}
                    ></div> */}
                    <div
                      className="status-bar-segment"
                      style={{
                        backgroundColor: "#F44336",
                        width: `${PRData.rejectedPercentage}%`,
                      }}
                    ></div>
                    <div
                      className="status-bar-segment"
                      style={{
                        backgroundColor: "#acbebe",
                        width: `${PRData.draftedPercentage}%`,
                      }}
                    ></div>
                  </div>

                  {/* Status Items */}
                  <div className="status-container">
                    <div className="status-item">
                      <div
                        className="status-indicator"
                        style={{ backgroundColor: "#4CAF50" }}
                      ></div>
                      <div className="status-label">Approved</div>
                      <div className="status-count">
                        {PRData.approvedPRs} PR Request
                      </div>
                      {/* <div className="status-percentage orange">(36%)</div> */}
                    </div>

                    <div className="status-item">
                      <div
                        className="status-indicator"
                        style={{ backgroundColor: "#2196F3" }}
                      ></div>
                      <div className="status-label">Pending</div>
                      <div className="status-count">
                        {PRData.pending} PR Request
                      </div>
                      {/* <div className="status-percentage blue">(25%)</div> */}
                    </div>

                    <div className="status-item">
                      <div
                        className="status-indicator"
                        style={{ backgroundColor: "#a72d24" }}
                      ></div>
                      <div className="status-label">Returned</div>
                      <div className="status-count">
                        {PRData.returned} PR Request
                      </div>
                      {/* <div className="status-percentage red">(16%)</div> */}
                    </div>
                    <div className="status-item">
                      <div
                        className="status-indicator"
                        style={{ backgroundColor: "#f44336" }}
                      ></div>
                      <div className="status-label">Rejected</div>
                      <div className="status-count">
                        {PRData.rejected} PR Request
                      </div>
                      {/* <div className="status-percentage red">(16%)</div> */}
                    </div>
                    <div className="status-item">
                      <div
                        className="status-indicator"
                        style={{ backgroundColor: "#acbebe" }}
                      ></div>
                      <div className="status-label">Draft</div>
                      <div className="status-count">
                        {PRData.draft} PR Request
                      </div>
                      {/* <div className="status-percentage red">(16%)</div> */}
                    </div>
                  </div>

                  {/* Status Bar */}
                  {/* <div className="status-bar-container">
                    <div
                      className="status-bar-segment"
                      style={{ backgroundColor: "#4CAF50", width: `${PRData.draft}%` }}
                    ></div>
                    <div
                      className="status-bar-segment"
                      style={{ backgroundColor: "#2196F3", width: `${PRData.pending}%` }}
                    ></div>
                    <div
                      className="status-bar-segment"
                      style={{ backgroundColor: "#FF9800", width: `${PRData.approvedPRs}%` }}
                    ></div>
                    <div
                      className="status-bar-segment"
                      style={{ backgroundColor: "#f44336", width: `${PRData.returned}%` }}
                    ></div>
                      <div
                      className="status-bar-segment"
                      style={{ backgroundColor: "#f443385", width: `${PRData.rejected}%` }}
                    ></div>
                  </div> */}
                </div>
              </div>
              <div className="dashboard-panel box-4">
                <div className="panel-header">
                  <div className="header-left">
                    <div
                      className="p-indicator"
                      // style={{ backgroundColor: "#9C27B0" }}
                    ></div>
                    <div className="maintenace-header-text">
                      Preventive schedules
                    </div>
                  </div>
                </div>
                <div className="header-separators"></div>
                {/* <div className="permit-body">
                  
                  <div
                    className="donut-chart"
                    style={{
                      width: "290px",
                      height: "254px",
                      // marginLeft: "15px",
                    }}
                  >
                    {isNoDataAvailable ? (
                      <div className="no-data-message">No Data Available</div>
                    ) : (
                      <>
                        <Doughnut
                          data={doughnutChartData}
                          options={doughnutChartOptions}
                        />
                        <div
                          className="detail-chart"
                          style={{
                            textAlign: "center",
                            fontSize: "25px",
                            fontWeight: "bold",
                          }}
                        >
                          {chartData ? chartData.total_schedules : 0} <br />{" "}
                          Schedules
                        </div>
                      </>
                    )}
                  </div>

                
                </div> */}
                <div className="permit-body chart-card">
                  <div className="chart-donut-wrapper">
                    <Doughnut
                      data={doughnutChartData}
                      options={doughnutChartOptions}
                    />
                    <div className="chart-center-label">
                      <div>Total</div>
                      <div className="chart-total">
                        {formatNumber(chartData?.total_schedules ?? 0)}
                      </div>
                    </div>
                  </div>

                  <div className="chart-legend">
                    {labels.map((label, index) => (
                      <div className="legend-row" key={label}>
                        <span
                          className="legend-dot"
                          style={{ backgroundColor: colors[index] }}
                        ></span>
                        <span className="legend-label">{label}</span>
                        {/* <span className="legend-value">{values[index]}</span> */}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard-container-third-part">
              {/* Maintenance Metrics Panel */}

              <div className="first-part">
                <div className="panel-header">
                  <div className="header-left">
                    <div className="pr-title-indicator"></div>
                    <div className="maintenace-header-text">
                      Procurement Metrics
                    </div>
                  </div>
                  {/* <div className="view-all">
                    <span>View All</span>
                    <span className="arrow">›</span>
                  </div> */}
                </div>
                <div className="header-separators"></div>
                <div className="pr-body-part">
                  <div className="pr-metrics-grid">
                    {/* First row */}

                    <div className="pr-metric-card">
                      <div className="pr-metric-header">
                        <span className="pr-metric-label">
                          Total Purchase Requests
                        </span>
                        <TooltipLogic
                          largIcons
                          tooltiplogic="The total number of purchase requests created within the selected date range."
                        />
                        {/* <span className="metric-change pr-positive">+18%</span> */}
                      </div>
                      <div className="pr-metric-value">
                        {formatNumber(PRData.totalPRs)}
                      </div>
                      <div className="pr-progress-bar">
                        <div
                          className="pr-progress"
                          style={{ width: "75%" }}
                        ></div>
                      </div>
                    </div>

                    <div className="pr-metric-card">
                      <div className="pr-metric-header">
                        <span className="pr-metric-label">
                          Approved PR Rate
                        </span>
                        <TooltipLogic
                          largIcons
                          // tooltiplogic="Percentage of approved PRs out of the total PRs. Formula: (Approved PRs / Total PRs) × 100"
                          tooltiplogic={
                            <>
                              Percentage of approved PRs out of the total PRs.
                              <br />
                              <strong>Formula:</strong>
                              <br />
                              <code
                                style={{
                                  background: "#f5f5f5",
                                  padding: "2px 4px",
                                  borderRadius: "4px",
                                  display: "inline-block",
                                }}
                              >
                                Formula: (Approved PRs / Total PRs) × 100
                              </code>
                            </>
                          }
                        />
                        {/* <span className="metric-change pr-positive">+5.2%</span> */}
                      </div>
                      <div className="pr-metric-value">
                        {PRData.approvedRate}%
                      </div>
                      <div className="pr-progress-bar">
                        <div
                          className="pr-progress"
                          style={{ width: `${PRData.approvedRate}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="pr-metric-card">
                      <div className="pr-metric-header">
                        <span className="pr-metric-label">
                          AVG PR Approval Time
                        </span>
                        <TooltipLogic
                          largIcons
                          tooltiplogic="The average time taken for a PR to get approved after it was created."
                        />
                        {/* <span className="pr-metric-change pr-negative">
                          +0.8 days
                        </span> */}
                      </div>
                      <div className="pr-metric-value">
                        {formatHoursToDays(PRData.avgApprovalTime)}
                      </div>
                      <div className="pr-progress-bar">
                        <div
                          className="pr-progress"
                          style={{ width: "60%" }}
                        ></div>
                      </div>
                    </div>

                    {/* Second row */}
                    <div className="pr-metric-card">
                      <div className="pr-metric-header">
                        <span className="pr-metric-label">
                          PR to PO Conversion Rate
                        </span>
                        <TooltipLogic
                          largIcons
                          //                           tooltiplogic="Percentage of PRs that resulted in a Purchase Order (PO).
                          //  Formula: (No. of PRs linked to POs / Total PRs) × 100"
                          tooltiplogic={
                            <>
                              Percentage of PRs that resulted in a Purchase
                              Order (PO).
                              <br />
                              <strong>Formula:</strong>
                              <br />
                              <code
                                style={{
                                  background: "#f5f5f5",
                                  padding: "2px 4px",
                                  borderRadius: "4px",
                                  display: "inline-block",
                                }}
                              >
                                (No. of PRs linked to POs / Total PRs) × 100
                              </code>
                            </>
                          }
                        />
                        {/* <span className="metric-change pr-positive">+3.5%</span> */}
                      </div>
                      <div className="pr-metric-value">
                        {PRData.prToPOConversionRate}%
                      </div>
                      <div className="pr-progress-bar">
                        <div
                          className="pr-progress"
                          // style={{ width: "76.4%" }}
                          style={{ width: `${PRData.prToPOConversionRate}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="pr-metric-card">
                      <div className="pr-metric-header">
                        <span className="pr-metric-label">
                          Total Purchase Orders
                        </span>
                        <TooltipLogic
                          largIcons
                          tooltiplogic="The total number of Purchase Orders created within the selected date range."
                        />
                        {/* <span className="metric-change pr-positive">+14%</span> */}
                      </div>
                      <div className="pr-metric-value">
                        {formatNumber(POData.total_pos)}
                      </div>
                      <div className="pr-progress-bar">
                        <div
                          className="pr-progress"
                          style={{ width: "70%" }}
                        ></div>
                      </div>
                    </div>

                    <div className="pr-metric-card">
                      <div className="pr-metric-header">
                        <span className="pr-metric-label">
                          AVG PO Processing Days
                        </span>
                        <TooltipLogic
                          largIcons
                          //                           tooltiplogic="The average number of days it takes to create a Purchase Order after a Purchase Request is approved.
                          //  Formula:
                          //  (PO Creation Date - PR Approval Date) averaged over all POs."
                          tooltiplogic={
                            <>
                              The average number of days it takes to create a
                              Purchase Order after a Purchase Request is
                              approved.
                              <br />
                              <strong>Formula:</strong>
                              <br />
                              <code
                                style={{
                                  background: "#f5f5f5",
                                  padding: "2px 4px",
                                  borderRadius: "4px",
                                  display: "inline-block",
                                }}
                              >
                                (PO Creation Date - PR Approval Date) averaged
                                over all POs.
                              </code>
                            </>
                          }
                        />
                        {/* <span className="metric-change pr-positive">
                          -1.2 days
                        </span> */}
                      </div>
                      <div className="pr-metric-value">
                        {formatHoursToDays(POData.avg_po_processing_days)}
                      </div>
                      <div className="pr-progress-bar">
                        <div
                          className="pr-progress"
                          style={{ width: "65%" }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  {/* Status Distribution */}
                  {/* <div className="status-section">
                  <div className="status-header">PR Status Distribution</div>
                  <div className="status-bar-container">
                    <div
                      className="status-bar draft"
                      style={{ width: "30%" }}
                    ></div>
                    <div
                      className="status-bar review"
                      style={{ width: "25%" }}
                    ></div>
                    <div
                      className="status-bar approved"
                      style={{ width: "25%" }}
                    ></div>
                    <div
                      className="status-bar onhold"
                      style={{ width: "10%" }}
                    ></div>
                    <div
                      className="status-bar rejected"
                      style={{ width: "10%" }}
                    ></div>
                  </div>
                  <div className="status-legend">
                    <div className="legend-item">
                      <span className="legend-color draft"></span>
                      <span className="legend-text">Draft {PRData.draft}</span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-color review"></span>
                      <span className="legend-text">
                        Pending {PRData.pending}
                      </span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-color approved"></span>
                      <span className="legend-text">
                        Approved {PRData.approvedPRs}
                      </span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-color onhold"></span>
                      <span className="legend-text">
                        Returned {PRData.returned}
                      </span>
                    </div>
                    <div className="legend-item">
                      <span className="legend-color rejected"></span>
                      <span className="legend-text">
                        Rejected {PRData.rejected}
                      </span>
                    </div>
                  </div>
                </div> */}
                </div>
              </div>
              {/* <div className="second-part">
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
